import React, { useEffect, useMemo, useState } from 'react';
import { INK, STATUS, BRAND } from '../../Utils/styleTokens';
import { uiLabel, KNOWN_ENTITY_CODES } from '../../Utils/Labeling';
import SortableTh, { sortRows } from '../../Utils/SortableTh';
import { getAdvancedConfig, saveAdvancedConfig, SEVERITY_LEVELS } from '../api';

const UNASSIGNED = 'UNASSIGNED';
const TIER_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, [UNASSIGNED]: 4 };
const SORT_ACCESSORS = {
  entity: (r) => r.label,
  code: (r) => r.code,
  tier: (r) => TIER_RANK[r.level] ?? 9,
};

const LEVEL_META = {
  CRITICAL: { label: 'Critical', color: STATUS.danger.text, bg: STATUS.danger.bg,
    help: 'Grants access or money directly — secrets, keys, card data. Any hit should be acted on now.' },
  HIGH: { label: 'High', color: STATUS.orange.text, bg: STATUS.orange.bg,
    help: 'Regulated identifiers that are hard to rotate — government IDs, bank accounts, clinical identifiers.' },
  MEDIUM: { label: 'Medium', color: STATUS.warning.text, bg: STATUS.warning.bg,
    help: 'Contact, date and location data — PII, but rarely a standalone breach.' },
  LOW: { label: 'Low', color: BRAND.blue, bg: STATUS.info.bg,
    help: 'Names, organisations and geography — noise on their own.' },
};

const levelOf = (tiers, code) => {
  const tier = tiers.find((t) => t.entities.includes(code));
  return tier ? tier.level : UNASSIGNED;
};

// Landscape Severity screen: assign entity types to the four Enterprise Data Landscape tiers and
// set each tier's minimum summed finding count. EDL-only — the Data Privacy Workflow risk score
// does not read this. Saved through the shared read-modify-write advanced-config PUT.
const LandscapeSeverityScreen = ({ canEdit }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [filter, setFilter] = useState('');
  // Tier filter chips: empty = every tier; otherwise only rows whose tier is in the list.
  const [levelFilter, setLevelFilter] = useState([]);
  const [sort, setSort] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await getAdvancedConfig();
        if (!alive) return;
        setTiers(cfg.landscapeSeverity.tiers);
        setDefaults(cfg.landscapeSeverityDefaults.tiers);
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load landscape severity config');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Every code the UI knows plus anything the stored config mentions (custom detector labels).
  const rows = useMemo(() => {
    const codes = new Set(KNOWN_ENTITY_CODES);
    tiers.forEach((t) => t.entities.forEach((c) => codes.add(c)));
    const q = filter.trim().toLowerCase();
    const base = Array.from(codes)
      .map((code) => ({ code, label: uiLabel(code), level: levelOf(tiers, code) }))
      .filter((r) => !q || r.code.toLowerCase().includes(q) || r.label.toLowerCase().includes(q))
      .filter((r) => levelFilter.length === 0 || levelFilter.includes(r.level))
      .sort((a, b) => a.label.localeCompare(b.label));
    return sortRows(base, sort, SORT_ACCESSORS);
  }, [tiers, filter, levelFilter, sort]);

  const toggleLevel = (level) => {
    setLevelFilter((prev) => (prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]));
  };

  const assign = (code, level) => {
    setMessage(null);
    setTiers((prev) => prev.map((t) => {
      const without = t.entities.filter((c) => c !== code);
      return t.level === level ? { ...t, entities: [...without, code] } : { ...t, entities: without };
    }));
  };

  const setMinFindings = (level, raw) => {
    setMessage(null);
    const n = Number(raw);
    setTiers((prev) => prev.map((t) => (t.level === level
      ? { ...t, minFindings: Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1 }
      : t)));
  };

  const reset = () => {
    setMessage(null);
    setTiers(defaults.map((t) => ({ ...t, entities: [...t.entities] })));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await saveAdvancedConfig({ landscapeSeverity: { tiers } });
      if (saved && saved.landscapeSeverity) setTiers(saved.landscapeSeverity.tiers);
      setMessage('Saved. The Enterprise Data Landscape re-colours on its next refresh.');
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const counts = SEVERITY_LEVELS.map((level) => ({
    level, count: (tiers.find((t) => t.level === level) || { entities: [] }).entities.length,
  }));

  return (
    <div>
      <h1 className='adv-config-screen-title'>Landscape Severity</h1>
      <p className='adv-config-screen-desc'>
        Colours the <strong>Enterprise Data Landscape</strong>. A data source takes the highest tier whose
        entities add up to at least that tier&apos;s <strong>minimum findings</strong> in its latest completed
        discovery; no tier hit means <strong>clean</strong>, no completed discovery means <strong>never scanned</strong>.
        Entities left unassigned never affect severity. This scale is for IT teams managing exposure across
        shares, buckets and SharePoint — it does not change the Data Privacy Workflow risk score,
        discovery, or masking.
      </p>

      <div className='adv-config-card'>
        <table className='adv-config-table' data-testid='severity-tiers'>
          <thead>
            <tr>
              <th style={{ width: '18%' }}>Tier</th>
              <th style={{ width: '16%' }}>Minimum findings</th>
              <th style={{ width: '12%' }}>Entities</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            {SEVERITY_LEVELS.map((level) => {
              const tier = tiers.find((t) => t.level === level) || { minFindings: 1, entities: [] };
              const meta = LEVEL_META[level];
              return (
                <tr key={level}>
                  <td>
                    <span className='sg-severity' style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                  </td>
                  <td>
                    <input
                      type='number' min={1} step={1}
                      className='adv-config-search'
                      style={{ width: 90 }}
                      data-testid={`min-findings-${level}`}
                      value={tier.minFindings}
                      disabled={!canEdit || saving || loading}
                      onChange={(e) => setMinFindings(level, e.target.value)}
                    />
                  </td>
                  <td style={{ color: INK[500] }}>{counts.find((c) => c.level === level)?.count ?? 0}</td>
                  <td style={{ color: INK[500], fontSize: 12 }}>{meta.help}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className='adv-config-card' style={{ marginTop: 16 }}>
        <div className='adv-config-toolbar'>
          <input
            data-testid='filter-severity-entities'
            className='adv-config-search'
            placeholder='Filter entities…'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className='adv-config-chips' role='group' aria-label='Filter by tier' data-testid='severity-level-filters'>
            <button type='button'
              className={`adv-config-chip${levelFilter.length === 0 ? ' adv-config-chip--on' : ''}`}
              onClick={() => setLevelFilter([])} data-testid='severity-filter-ALL'>
              All
            </button>
            {SEVERITY_LEVELS.map((level) => {
              const on = levelFilter.includes(level);
              const meta = LEVEL_META[level];
              return (
                <button key={level} type='button'
                  className={`adv-config-chip${on ? ' adv-config-chip--on' : ''}`}
                  style={on ? { background: meta.bg, color: meta.color, borderColor: meta.color } : undefined}
                  aria-pressed={on}
                  onClick={() => toggleLevel(level)} data-testid={`severity-filter-${level}`}>
                  {meta.label} {counts.find((c) => c.level === level)?.count ?? 0}
                </button>
              );
            })}
          </div>
          <span style={{ flex: 1 }} />
          {canEdit && (
            <button type='button' className='adv-config-btn-secondary adv-config-btn'
              onClick={reset} disabled={saving || loading} data-testid='severity-reset'>
              Reset to defaults
            </button>
          )}
        </div>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className='adv-config-table' data-testid='severity-entities'>
            <thead>
              <tr>
                <SortableTh label='Entity' sortKey='entity' sort={sort} onSort={setSort} style={{ width: '40%', padding: '9px 12px' }} />
                <SortableTh label='Code' sortKey='code' sort={sort} onSort={setSort} style={{ width: '25%', padding: '9px 12px' }} />
                <SortableTh label='Tier' sortKey='tier' sort={sort} onSort={setSort} style={{ padding: '9px 12px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ code, label, level }) => (
                <tr key={code} data-testid={`severity-row-${code}`}>
                  <td>{label}</td>
                  <td style={{ color: INK[400], fontSize: 11 }}>{code}</td>
                  <td>
                    <select className='adv-config-select' value={level} disabled={!canEdit || saving}
                      aria-label={`Tier for ${label}`}
                      onChange={(e) => assign(code, e.target.value)}>
                      {SEVERITY_LEVELS.map((l) => (
                        <option key={l} value={l}>{LEVEL_META[l].label}</option>
                      ))}
                      <option value={UNASSIGNED}>Unassigned</option>
                    </select>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ color: INK[500] }}>
                    {filter.trim() ? `No entities match “${filter}”` : 'No entities'}
                    {levelFilter.length > 0 ? ` in the ${levelFilter.map((l) => LEVEL_META[l].label).join(' / ')} tier${levelFilter.length === 1 ? '' : 's'}` : ''}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className='adv-config-toolbar'>
        <button type='button' className='adv-config-btn' onClick={save} disabled={!canEdit || saving || loading}
          data-testid='severity-save'>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {message && <span className='adv-config-msg-ok'>{message}</span>}
        {error && <span className='adv-config-msg-err'>{error}</span>}
      </div>
    </div>
  );
};

export default LandscapeSeverityScreen;
